import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Clock, FileText, ShieldCheck, AlertTriangle, RotateCcw, XCircle, Play,
} from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, ErrorState, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Collapsible } from '@/acc-ui/primitives/Collapsible';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { ingestionApi, type LoanFileDocument, type LoanFileEvent } from '@/api/ingestion-api';
import { parsingApi } from '@/api/parsing-api';
import { validationApi } from '@/api/validation-api';
import { ParsingResultsPanel } from './parsing/ParsingResultsPanel';
import { FindingsPanel } from './validation/FindingsPanel';
import { ComplianceGrid } from './los/ComplianceGrid';
// Banking Verification panel hidden for now — see render site below.
// import { BankingVerificationPanel } from './banking/BankingVerificationPanel';
import { StateBadge } from './loan-files';
import { usePermission } from '@/auth/use-permission';
import { INGESTION_DELETE, INGESTION_RETRIGGER, LOAN_FILE_STATE, PARSING_REPARSE } from '@loanaudit/shared';
import { cn } from '@/lib/utils';


// Display label maps for product / branch codes (friendly UI labels)
const PRODUCT_DISPLAY: Record<string, string> = {
  HOME_LOAN: 'Home Loan',
  PL: 'Personal Loan',
  BL: 'Business Loan',
  TL: 'Term Loan',
  WC: 'Working Capital',
  BIZ: 'Business Loan',
  AUTO: 'Auto Loan',
  LAP: 'Loan Against Property',
  DISHA_HL: 'Disha Home Loan',
  MICRO_LAP: 'Micro LAP',
  ML: 'Micro Loan',
};

const BRANCH_DISPLAY: Record<string, string> = {
  MUM_BKC: 'Mumbai — BKC',
  DEL_CP: 'Delhi — Connaught Place',
  BLR_KOR: 'Bangalore — Koramangala',
  CHE_TNG: 'Chennai — T. Nagar',
  KOL_PRK: 'Kolkata — Park Street',
  PUN_KOR: 'Pune — Koregaon Park',
};

const SOURCE_DISPLAY: Record<string, string> = {
  upload: 'Upload',
  api: 'API',
  connector: 'Connector',
};

/** Compact date+time without seconds, e.g. "16 Jun 2026, 9:11 am". */
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function LoanFileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { has } = usePermission();

  const canReject = has(INGESTION_DELETE);
  const canRetrigger = has(INGESTION_RETRIGGER);
  const canReparse = has(PARSING_REPARSE);

  const query = useQuery({
    queryKey: ['loanFile', id],
    queryFn: () => ingestionApi.get(id!),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      // Poll while moving through the pipeline; stop once terminal.
      // Sprint 5: parsing_in_progress / parsing_failed / parsed are also
      // non-terminal or terminal respectively — parsed and rejected end
      // the poll; parsing_failed too (auditor retriggers manually).
      const state = q.state.data?.state;
      if (!state) return false;
      const terminal: readonly string[] = [
        LOAN_FILE_STATE.PARSED,
        LOAN_FILE_STATE.PARSING_FAILED,
        LOAN_FILE_STATE.REJECTED,
      ];
      return terminal.includes(state) ? false : 5000;
    },
  });

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const reject = useMutation({
    mutationFn: (reason: string) => ingestionApi.reject(id!, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loanFile', id] });
      qc.invalidateQueries({ queryKey: ['loanFiles'] });
      setRejectOpen(false);
      setRejectReason('');
    },
  });

  const retrigger = useMutation({
    mutationFn: () => ingestionApi.retrigger(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loanFile', id] });
      qc.invalidateQueries({ queryKey: ['loanFiles'] });
    },
  });

  const reparse = useMutation({
    mutationFn: (stage: 'ocr' | 'classify' | 'extract' | 'all') =>
      parsingApi.retrigger(id!, { stage, reason: 'Manual retry from loan file detail' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loanFile', id] });
    },
  });

  // Re-run audit — fires the deterministic validation engine over the CAM
  // and LOS data already on the loan file. Used after a masters config
  // change (e.g. CIBIL threshold fix) so verdicts get recomputed without
  // re-uploading the LOS JSON. No-op if there's no CAM or no LOS yet —
  // the backend returns a clear error in those cases.
  const rerunAudit = useMutation({
    mutationFn: () =>
      validationApi.runValidation(id!, {
        reason: 'Manual re-run from loan file detail',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loanFile', id] });
      qc.invalidateQueries({ queryKey: ['validation', id] });
      qc.invalidateQueries({ queryKey: ['findings'] });
    },
  });

  if (query.isError) {
    return (
      <AppShell crumb="Workspace" title="Loan file">
        <ErrorState
          description="Could not load loan file."
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      </AppShell>
    );
  }
  if (!query.data) {
    return (
      <AppShell crumb="Workspace" title="Loan file">
        <Card><div className="py-4 text-center text-text-tertiary text-label">Loading…</div></Card>
      </AppShell>
    );
  }

  const f = query.data;
  const isRejected = f.state === LOAN_FILE_STATE.REJECTED;
  // Parsing can be re-run if parsing has run before (success or failure)
  // or while in progress (auditor retries a doc that's stuck).
  // Validation states also qualify — re-run parsing resets the pipeline.
  // Re-run audit is meaningful once parsing is done — i.e. CAM is parsed
  // and LOS data may also be attached. We don't gate on LOS presence here;
  // the backend will return an error if LOS is missing, and the message
  // surfaces in the mutation's error toast.
  const canShowRerunAudit =
    canReparse &&
    (f.state === LOAN_FILE_STATE.PARSED ||
     f.state === LOAN_FILE_STATE.ACCEPTED ||
     f.state === LOAN_FILE_STATE.VALIDATION_FAILED ||
     f.state === LOAN_FILE_STATE.VALIDATED ||
     f.state === LOAN_FILE_STATE.VALIDATION_IN_PROGRESS);

  const canShowReparse =
    canReparse &&
    (f.state === LOAN_FILE_STATE.PARSING_FAILED ||
     f.state === LOAN_FILE_STATE.PARSED ||
     f.state === LOAN_FILE_STATE.PARSING_IN_PROGRESS ||
     f.state === LOAN_FILE_STATE.ACCEPTED ||
     f.state === LOAN_FILE_STATE.VALIDATION_FAILED ||
     f.state === LOAN_FILE_STATE.VALIDATED ||
     f.state === LOAN_FILE_STATE.VALIDATION_IN_PROGRESS);

  return (
    <AppShell crumb="Workspace" title="Loan file">
      <PageHeader
        title={f.loanNumber}
        subtitle={f.borrowerName}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="w-4 h-4" />}
              onClick={() => navigate('/loan-files')}
            >
              Back
            </Button>
            {canShowReparse && (
              <Button
                size="sm"
                leftIcon={<RotateCcw className="w-4 h-4" />}
                onClick={() => reparse.mutate('ocr')}
                loading={reparse.isPending}
              >
                Re-run parsing
              </Button>
            )}
            {canShowRerunAudit && (
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Play className="w-3.5 h-3.5" />}
                onClick={() => rerunAudit.mutate()}
                loading={rerunAudit.isPending}
                title="Re-run the 12-point audit using the current CAM + LOS + masters config. Useful after editing masters (e.g. CIBIL thresholds)."
              >
                Re-run Audit
              </Button>
            )}
            {canRetrigger && isRejected && (
              <Button
                size="sm"
                leftIcon={<RotateCcw className="w-4 h-4" />}
                onClick={() => retrigger.mutate()}
                loading={retrigger.isPending}
              >
                Retrigger
              </Button>
            )}
            {canReject && !isRejected && f.state !== LOAN_FILE_STATE.ACCEPTED && (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<XCircle className="w-4 h-4" />}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            )}
          </div>
        }
      />

      {/* Summary strip */}
      <Card padding={false}>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-glass-hairline">
          <InfoCell label="State">
            <StateBadge state={f.state} rejectReason={f.rejectReason} />
          </InfoCell>
          <InfoCell label="Product">
            <span className="text-label font-medium text-text-primary">
              {PRODUCT_DISPLAY[f.productCode] ?? f.productCode}
            </span>
          </InfoCell>
          <InfoCell label="Branch">
            <span className="text-label font-medium text-text-primary">
              {BRANCH_DISPLAY[f.branchCode] ?? f.branchCode}
            </span>
          </InfoCell>
          <InfoCell label="Source">
            <Badge tone="muted">{SOURCE_DISPLAY[f.source] ?? f.source}</Badge>
          </InfoCell>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-glass-hairline border-t border-glass-hairline">
          {f.officerId && (
            <InfoCell label="Officer">
              <span className="text-caption text-text-primary">{f.officerId}</span>
            </InfoCell>
          )}
          <InfoCell label="Received">
            <span className="text-caption tabular-nums">{fmtDateTime(f.receivedAt)}</span>
          </InfoCell>
          {f.submittedAt && (
            <InfoCell label="Submitted">
              <span className="text-caption tabular-nums">{fmtDateTime(f.submittedAt)}</span>
            </InfoCell>
          )}
          {f.acceptedAt && (
            <InfoCell label="Accepted">
              <span className="text-caption tabular-nums">{fmtDateTime(f.acceptedAt)}</span>
            </InfoCell>
          )}
        </div>
      </Card>

      {/* Rejection banner */}
      {isRejected && f.rejectReason && (
        <Card className="border-red-200 bg-red-50/40">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-label font-semibold text-red-800">Rejected</div>
              <div className="text-caption text-red-800 mt-0.5">{f.rejectReason}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Critical Findings Banner (Gap 5) — prominent red/amber banner
          for open critical/high findings. Renders nothing when clean.
          No wrapper div: when the banner returns null it must not leave an
          empty flex item consuming column gap (a layout-shift source). */}
      <CriticalFindingsBanner loanFileId={f.id} />

      {/* SCORING — the 12-point compliance grid leads the page. */}
      <div className="mt-2.5">
        <ComplianceGrid loanFileId={f.id} />
      </div>

      {/* FINDINGS — directly under the scoring summary. */}
      <div className="mt-2.5">
        <FindingsPanel loanFileId={f.id} />
      </div>

      {/* LOS data panel removed — the LOS payload is now attached during the
          upload flow (loan number + LOS file), so the in-loan-file upload /
          replace section is redundant. */}
      {/* Banking Verification panel hidden for now (uses the mock Novel Check
          provider; not surfaced to users yet). Restore when a real provider
          is wired. The audit rules + report still consume any verification
          that exists. */}
      {/* <div className="mt-2.5">
        <BankingVerificationPanel loanFileId={f.id} documents={f.documents} />
      </div> */}

      {/* Secondary detail — collapsed dropdowns to keep the page focused. */}
      <ParsingResultsPanel loanFileId={f.id} />

      <Collapsible title="Documents" count={f.documents.length}>
        <ul className="divide-y divide-glass-hairline">
          {f.documents.map((d) => (
            <li key={d.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-text-muted shrink-0" />
                <div className="min-w-0">
                  <div className="text-label font-medium text-text-primary truncate">
                    {d.originalFileName}
                  </div>
                  <div className="text-micro text-text-tertiary">
                    {d.mimeType} · {formatBytes(d.fileSizeBytes)}
                    {d.sha256Hex && <span> · <code className="font-mono">{d.sha256Hex.slice(0, 12)}…</code></span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Only surface the upload state when something needs
                    attention — a successful upload is implied by the file
                    being listed and parsed. */}
                {d.uploadState !== 'uploaded' && (
                  <UploadStateBadge state={d.uploadState} />
                )}
                <ScanStateBadge state={d.scanState} engine={d.scanEngine} />
                {d.documentType && d.documentType !== 'unclassified' && (
                  <Badge tone="muted">{d.documentType}</Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Collapsible>

      <Collapsible title="Timeline">
        <ul className="divide-y divide-glass-hairline">
          {f.events.map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </ul>
      </Collapsible>

      {/* Reject dialog */}
      {rejectOpen && (
        <Dialog
          open
          onOpenChange={(v) => !v && setRejectOpen(false)}
          title="Reject loan file"
          description="This transitions the file to rejected state. It can be retriggered later if the issue is resolved."
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setRejectOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => reject.mutate(rejectReason.trim())}
                loading={reject.isPending}
                disabled={!rejectReason.trim()}
              >
                Reject
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-1">
            <span className="text-caption font-semibold text-text-secondary">
              Reason <span className="text-red-700">*</span>
            </span>
            <Textarea
              rows={4}
              placeholder="Why is this being rejected?"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </label>
        </Dialog>
      )}
    </AppShell>
  );
}

function InfoCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-4 py-3 min-w-0">
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function UploadStateBadge({ state }: { state: LoanFileDocument['uploadState'] }) {
  if (state === 'uploaded') return <Badge tone="success" dot>uploaded</Badge>;
  if (state === 'failed') return <Badge tone="danger" dot>upload failed</Badge>;
  return <Badge tone="warning" dot>pending</Badge>;
}

function ScanStateBadge({
  state,
  engine,
}: {
  state: LoanFileDocument['scanState'];
  engine: string | null;
}) {
  if (state === 'clean') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-micro font-semibold bg-emerald-500/15 text-emerald-800">
        <ShieldCheck className="w-3 h-3" /> clean{engine && <span className="font-normal opacity-80">·{engine}</span>}
      </span>
    );
  }
  if (state === 'infected') return <Badge tone="danger" dot>infected</Badge>;
  if (state === 'error') return <Badge tone="warning" dot>scan error</Badge>;
  return <Badge tone="muted" dot>not scanned</Badge>;
}

/** snake_case code → readable label, e.g. "parsing_in_progress" → "Parsing in progress". */
function humanize(code: string | null | undefined): string {
  if (!code) return '—';
  return code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function EventRow({ event }: { event: LoanFileEvent }) {
  const isTransition = event.eventType.startsWith('state_transition');
  return (
    <li className="px-4 py-3 flex items-start gap-3">
      <Clock className="w-4 h-4 text-text-muted shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {isTransition ? (
            <span className="text-caption font-medium text-text-primary">
              {humanize(event.fromState)} → {humanize(event.toState)}
            </span>
          ) : (
            <span className="text-caption font-medium text-text-primary">
              {humanize(event.eventType)}
            </span>
          )}
          <Badge tone="muted">{event.actorKind}</Badge>
          <span className="text-micro text-text-tertiary tabular-nums">
            {new Date(event.createdAt).toLocaleString()}
          </span>
        </div>
        {event.reason && (
          <div className="mt-0.5 text-caption text-text-tertiary">{event.reason}</div>
        )}
      </div>
    </li>
  );
}

function formatBytes(bytes: number): string {
  const MB = 1024 * 1024;
  const KB = 1024;
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${(bytes / KB).toFixed(0)} KB`;
  return `${bytes} B`;
}

// unused export — suppress lint
void cn;


// =========================================================================
// CriticalFindingsBanner — Gap 5: Red flag banner for high/critical findings
// =========================================================================
// Self-contained component that queries validation data independently.
// Renders a prominent banner when open critical/high findings exist.
// Renders nothing when there are no critical issues — zero visual noise.

function CriticalFindingsBanner({ loanFileId }: { loanFileId: string }) {
  const navigate = useNavigate();
  const vQuery = useQuery({
    queryKey: ['validation', loanFileId],
    queryFn: () => validationApi.getLoanFileView(loanFileId),
    staleTime: 30_000,
  });

  const criticalFindings = useMemo(() => {
    const findings = vQuery.data?.findings ?? [];
    return findings.filter(
      (f) =>
        (f.severity === 'critical' || f.severity === 'high') &&
        f.status === 'open',
    );
  }, [vQuery.data]);

  if (criticalFindings.length === 0) return null;

  const critCount = criticalFindings.filter((f) => f.severity === 'critical').length;
  const highCount = criticalFindings.filter((f) => f.severity === 'high').length;

  const severityLabel = [
    critCount > 0 ? `${critCount} critical` : '',
    highCount > 0 ? `${highCount} high` : '',
  ].filter(Boolean).join(' · ');

  const hasCritical = critCount > 0;

  return (
    <Card
      className={
        hasCritical
          ? 'border-error/40 bg-error-container/40'
          : 'border-amber-300 bg-amber-50'
      }
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 mt-0.5 p-1.5 rounded-lg ${
            hasCritical ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-body font-semibold ${
                hasCritical ? 'text-red-800' : 'text-amber-800'
              }`}
            >
              {criticalFindings.length} issue{criticalFindings.length === 1 ? '' : 's'} need
              attention
            </span>
            <span
              className={`text-micro font-medium px-2 py-0.5 rounded-full ${
                hasCritical
                  ? 'bg-red-100 text-red-700'
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {severityLabel}
            </span>
          </div>

          <ul className="mt-2 space-y-1">
            {criticalFindings.slice(0, 5).map((f) => (
              <li key={f.id} className="flex items-center gap-2">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    f.severity === 'critical' ? 'bg-red-600' : 'bg-amber-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => navigate(`/findings/${f.id}`)}
                  className={`text-caption font-medium hover:underline text-left truncate ${
                    f.severity === 'critical'
                      ? 'text-red-800'
                      : 'text-amber-800'
                  }`}
                  title={f.description ?? f.title}
                >
                  {f.title}
                </button>
                <Badge
                  tone={f.severity === 'critical' ? 'danger' : 'warning'}
                  className="shrink-0 text-micro"
                >
                  {f.severity}
                </Badge>
              </li>
            ))}
            {criticalFindings.length > 5 && (
              <li className="text-caption text-text-tertiary pl-3.5">
                + {criticalFindings.length - 5} more finding
                {criticalFindings.length - 5 === 1 ? '' : 's'}
              </li>
            )}
          </ul>
        </div>
      </div>
    </Card>
  );
}

